import { useState, useRef, useEffect } from 'react'
import { useQueryClient } from 'react-query'
import { Modal } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { ArrowLeft } from 'blaze-ui/icons'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useReplaceMedia } from 'hooks/queries/designs/useReplaceMedia'
import { updateFileDetailCache } from 'hooks/queries/files/cacheUtils'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import type { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { useFile } from 'hooks/queries/files/useFile'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import type { ImageRefinedContent, VideoRefinedContent, RefinedContent } from '../RefineMediaModal'
import Styles from '../RefineMediaModal.module.scss'

interface RefinedMediaPreviewProps {
  isVideo: boolean
  refinedContent: RefinedContent | null
  designId: string
  onClose: () => void
  onBack: () => void
}

export function RefinedMediaPreview({ isVideo, refinedContent, designId, onClose, onBack }: RefinedMediaPreviewProps) {
  const [isProcessing, setIsProcessing] = useState(false)
  const dismissToastRef = useRef<(() => void) | null>(null)
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  const { mutateAsync: replaceMedia, isLoading: isReplacing } = useReplaceMedia({ designId })

  // Observes the file cache only while processing. When useDesignPreviewSync
  // (in PostPreview) updates the cache via websocket, React Query notifies
  // this observer and the effect below closes the modal.
  const { data: fileData } = useFile(designId, { enabled: isProcessing })

  useEffect(() => {
    if (!isProcessing || !fileData || fileData.image_url_generating) return

    setIsProcessing(false)
    dismissToastRef.current?.()
    showToastMessage('Design updated!', 'success')

    queryClient.invalidateQueries(filesKeys.detail(workspaceId, designId))
    onClose()
  }, [isProcessing, fileData, onClose, queryClient, workspaceId, designId])

  const isUpdating = isReplacing || isProcessing

  async function handleInsert() {
    if (!refinedContent || isUpdating) return

    dismissToastRef.current = showToastMessage('Updating design...', 'generating', undefined, 0)

    try {
      await replaceMedia({ media_file_key: refinedContent.media_file_id })
      updateFileDetailCache<AlmanacFileShowItem>(queryClient, workspaceId, designId, (file) => ({
        ...file,
        image_url_generating: true,
      }))
      setIsProcessing(true)
    } catch {
      dismissToastRef.current?.()
      showToastMessage('Failed to update design', 'error')
    }
  }

  return (
    <>
      <Modal.Header title={`Revised ${isVideo ? 'video' : 'image'}`} onClose={onClose} />
      <Modal.Content>
        <div className={Styles.resultContent}>
          {isVideo && refinedContent ? (
            <video
              src={(refinedContent as VideoRefinedContent)?.video_url}
              className={Styles.resultVideo}
              controls
              playsInline
            />
          ) : (
            <img
              src={(refinedContent as ImageRefinedContent)?.image_url}
              alt="Refined"
              className={Styles.resultMedia}
            />
          )}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button variant="tertiary" size="lg" frontIcon={ArrowLeft} onPress={onBack}>
            Back
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Button size="lg" onPress={handleInsert} isDisabled={isUpdating}>
            {isUpdating ? 'Updating...' : 'Insert into design'}
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
